import {
    Divider,
    Text,
    typographyStyles,
    makeStyles,
    List,
    ListItem,
    Subtitle2,
    tokens,
    Tag,
    TagPicker,
    TagPickerControl,
    TagPickerGroup,
    TagPickerInput,
    Field,
    TagPickerProps,
} from "@fluentui/react-components";
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useOfficeAppContext } from "../OfficeAppContext";
import { Backend } from "../../lib/backend";
import { Loading } from "../components/Loading";
import { BasicLayout } from "../components/BasicLayout";

const useStyles = makeStyles({
    root: {
        minHeight: "100vh",
        padding: "1rem",
    },
    title: typographyStyles.title2,
    title3: typographyStyles.subtitle1,
    loadingRoot: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
    },
    listItem: {
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            transition: "background-color 0.2s ease",
        },
    },
});

const REFETCH_INTERVAL = 60000; // 60 seconds

export const Dashboard = () => {
    const styles = useStyles();
    const { user, tasks, is_loading, setIsLoading, setTasks, setError } = useOfficeAppContext();
    const [limit] = useState(100);
    const [filters, setFilters] = useState<string[]>([]);
    const [input_value, setInputValue] = useState("");
    const has_initial_fetch_ref = useRef(false);

    const onOptionSelect: TagPickerProps["onOptionSelect"] = (_: any, data: any) => {
        setFilters(data.selectedOptions);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.currentTarget.value);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && input_value) {
            setInputValue("");
            setFilters((curr) => (curr.includes(input_value) ? curr : [...curr, input_value]));
        }
    };

    const on_open_task_details = (item: any) => async () => {
        // construct URL to open task details in main app
        const base_url = window.location.origin.replace(':3000', ':3000'); // adjust port as needed
        const url = `${base_url}/confirm/${item.uuid}`;

        // office-specific window opening logic
        const window_features =
            "width=1280,height=720,resizable=yes,scrollbars=yes,status=yes,menubar=no,toolbar=no,location=yes,noopener,noreferrer";

        if (typeof (globalThis as any).Office !== 'undefined') {
            const Office = (globalThis as any).Office;
            if (Office.context.platform === Office.PlatformType.OfficeOnline) {
                window.open(url, `task_${item.uuid}`, window_features);
            } else if (
                Office.context.platform === Office.PlatformType.PC ||
                Office.context.platform === Office.PlatformType.Mac
            ) {
                Office.context.ui.openBrowserWindow(url);
            }
        } else {
            // fallback for non-Office environments
            window.open(url, `task_${item.uuid}`, window_features);
        }
    };

    // initial data fetch using existing client backend
    useEffect(() => {
        // capture initial fetch state before async operation
        const is_initial_fetch = !has_initial_fetch_ref.current;

        if (is_initial_fetch) {
            setIsLoading(true);
        }

        Backend.getExtractJobs({ limit, offset: 0 }) // TODO: items, not jobs
            .then((response: any) => {
                setTasks(response.jobs || []);
                if (is_initial_fetch) {
                    has_initial_fetch_ref.current = true;
                }
            })
            .catch((error: any) => {
                setError(error.message);
            })
            .finally(() => {
                if (is_initial_fetch) {
                    setIsLoading(false);
                }
            });

        const interval_id = setInterval(() => {
            // background refresh
            Backend.getExtractJobs({ limit, offset: 0 })
                .then((response: any) => {
                    setTasks(response.jobs || []);
                })
                .catch((error: any) => {
                    console.error("Background refresh failed:", error.message);
                    // don't set error for background refreshes to avoid disrupting UX
                });
        }, REFETCH_INTERVAL);

        return () => {
            clearInterval(interval_id);
        };
    }, [limit, setError, setIsLoading, setTasks]);

    if (is_loading) {
        return <Loading />;
    }

    return (
        <BasicLayout title={`Welcome, ${user?.first_name || "User"}!`}>
            <div>
                <Text className={styles.title3} style={{ display: "block", margin: "0.5rem 0px" }}>
                    Tasks
                </Text>

                <Field label="Filter by name or template" style={{ width: "100%" }}>
                    <TagPicker noPopover onOptionSelect={onOptionSelect} selectedOptions={filters}>
                        <TagPickerControl>
                            <TagPickerGroup aria-label="Selected Filters">
                                {filters.map((option, index) => (
                                    <Tag key={index} shape="rounded" value={option}>
                                        {option}
                                    </Tag>
                                ))}
                            </TagPickerGroup>
                            <TagPickerInput
                                value={input_value}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                aria-label="Filter by name or template"
                            />
                        </TagPickerControl>
                    </TagPicker>
                </Field>
            </div>
            <List>
                {(tasks || [])
                    .filter((item) => {
                        if (filters.length === 0) {
                            return true;
                        }
                        return (
                            filters.some((filter) =>
                                item.name?.toLowerCase().includes(filter.toLowerCase())
                            ) ||
                            filters.some((filter) =>
                                item.template_name?.toLowerCase().includes(filter.toLowerCase())
                            )
                        );
                    })
                    .map((item) => (
                        <ListItem key={item.uuid} onClick={on_open_task_details(item)}>
                            <div
                                className={styles.listItem}
                                style={{
                                    padding: "0.5rem 0px",
                                    display: "flex",
                                    flexDirection: "column",
                                    cursor: "pointer",
                                }}
                            >
                                <Subtitle2>Name: {item.name || 'Unnamed Task'}</Subtitle2>
                                <Text>
                                    <span style={{ fontWeight: "bolder" }}>Template:</span> {item.template_name || 'Unknown'}
                                </Text>
                                <Text>
                                    <span style={{ fontWeight: "bolder" }}>Created:</span>{" "}
                                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Unknown'}
                                </Text>
                            </div>
                            <Divider />
                        </ListItem>
                    ))}
            </List>
        </BasicLayout>
    );
};